/**
 * Where sign-in should send someone back to.
 *
 * SIMS-9057: a coordinator following a link into the backoffice while signed
 * out was sent to sign in and then dropped on the dashboard, losing the record
 * they were trying to reach. Auth0 carries this back through `appState`, and
 * `onRedirectCallback` already knows how to use it — nothing was ever putting
 * a value in.
 */

/**
 * Paths that must never be returned to.
 *
 * `/callback` is Auth0's own landing page: returning there would hand the SDK
 * a second authorization response it has no code for, and the user would end
 * up back at sign-in. `/unauthorized` is where a rejected route sends people,
 * so returning to it would strand them on the rejection rather than on what
 * they asked for.
 */
const NEVER_RETURN_TO = ["/callback", "/unauthorized"];

/**
 * The location to resume after sign-in, or undefined when there is nothing
 * worth resuming.
 *
 * Undefined rather than "/" on purpose: `onRedirectCallback` distinguishes the
 * two, and a caller that always supplies a destination would override whatever
 * default it decides on.
 *
 * The query string is kept — a link to a filtered report is a different
 * destination from the report — and the hash with it.
 */
export const resolveReturnTo = (
	location: Pick<Location, "pathname" | "search" | "hash">,
): string | undefined => {
	const { pathname, search, hash } = location;

	if (!pathname || pathname === "/") return undefined;
	if (NEVER_RETURN_TO.some((path) => pathname.startsWith(path))) {
		return undefined;
	}

	return `${pathname}${search ?? ""}${hash ?? ""}`;
};
